import OrderBilling from './billing/OrderBilling';
import type { OrderBillingServices } from './billing/types';
import { WorkActuals } from './actuals/WorkActuals';
import type { WorkActualsServices } from './actuals/types';
import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactElement,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  TabList,
  Tab,
  tokens,
  type SelectTabEvent,
  type SelectTabData,
} from '@fluentui/react-components';
import { EditRegular } from '@fluentui/react-icons';
import WorkDetails from './WorkDetails';
import { useThemedTabs } from '../theme/useThemedTabs';
import { useWorkDetailsLayout } from './workDetailsLayout';
import { StatusBadge } from '../StatusBadge';
import PageHeader from '../Layout/PageHeader';
import { useBreadcrumbs } from '../Layout/useBreadcrumbs';
import { shellVar } from '../Layout/shellTheme';
import WorkActions from './WorkActions';
import WorkTabPanel from './WorkTabPanel';
import type { IEditItemToolbarButton, SaveItemFn } from '../EditItem';
import WorkComments from './WorkComments';
import WorkPhotos from './WorkPhotos';
import WorkOtherInfo from './WorkOtherInfo';
import WorkAvailability from './WorkAvailability';
import { WorkOfferDetails, WorkOffersList } from '../WorkOffer';
import type {
  WorkComponentProps,
  IWorkDetailsServices,
  IWorkCommentsServices,
  IWorkPhotosServices,
  IWorkOtherInfoServices,
  IWorkAvailabilityServices,
  IWorkOrderEditServices,
  IWorkStatusHistoryServices,
  IWorkAppointmentsServices,
} from './types';
import WorkAppointments from './WorkAppointments';
import WorkDeadlines, { type GetWorkSlas } from './WorkDeadlines';
import { useTenant } from '../TenantProvider';
import MokoCard from '../Layout/MokoCard';
import WorkOrderTab from './WorkOrderTab';
import WorkStatusHistory from './WorkStatusHistory';
import type { IWorkOfferServices } from '../WorkOffer';
import {
  OrderDetailsView,
  type GetOrderService,
  type GetOrderMetadataService,
} from './OrderDetailsDrawer';
import {
  findStatusOption,
  isEntityLocked,
  type IAvailableWorkAction,
} from '@moko/utils';
import { isContractorRole } from '@moko/utils/src/authHelper';
import { WORK_TABS, isWorkTabId, type WorkTabId } from './workTabs';
import { useObjectState } from '../hooks/useObjectState';
import { readInitialParams } from '../hooks/useSearchParamsState';
import { useSyncParamsToUrl } from '../hooks/useSyncParamsToUrl';
import useFetch from '../hooks/useFetch';
import ContentSkeleton from '../ContentSkeleton/ContentSkeleton';

type WorkUrlState = {
  tab: WorkTabId;
  offerId: number | null;
};

const parseOfferId = (raw: string | null): number | null => {
  const parsed = Number.parseInt(raw ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const parseTab = (raw: string | null): WorkTabId =>
  isWorkTabId(raw) ? raw : WORK_TABS.Details;

const PAGE_EDIT_TABS: WorkTabId[] = [
  WORK_TABS.Details,
  WORK_TABS.OtherInfo,
  WORK_TABS.Order,
];

const keepTab = (
  visited: Set<WorkTabId>,
  selected: WorkTabId,
  tab: WorkTabId
) => selected === tab || visited.has(tab);

type WorkStatus = {
  id: number;
  statusId?: number;
  name: string;
  code: string;
  color: string;
  icon: string;
  lockEntity?: boolean;
  useOnFormFinish?: boolean | null;
};

type WorkStatusAction = {
  id: number;
  name: string;
  actionLabel?: string | null;
  showAsAction?: boolean;
  allowReportDelivery?: boolean | string;
  color?: string;
  icon?: string;
};

const isStatusAction = (value: unknown): boolean =>
  value === true || value === 'true';

export type WorkLeaveHandle = {
  requestLeave: (leave: () => void) => void;
};

export interface WorkProps extends WorkComponentProps {
  workId: number;
  detailsServices: IWorkDetailsServices;
  commentsServices: IWorkCommentsServices;
  photosServices: IWorkPhotosServices;
  otherInfoServices: IWorkOtherInfoServices;
  showTimesTab?: boolean;
  timesTab?: React.ReactNode;
  showAssetsTab?: boolean;
  assetsTab?: React.ReactNode;
  showAvailabilityTab?: boolean;
  availabilityServices?: IWorkAvailabilityServices;
  showOffersTab?: boolean;
  offersServices?: IWorkOfferServices;
  showQualityCheckTab?: boolean;
  qualityCheckTab?: React.ReactNode;
  showReportTab?: boolean;
  reportTab?: React.ReactNode;
  showHeader?: boolean;
  useDesktopSplitOtherInfo?: boolean;
  otherInfoReadOnly?: boolean;
  otherInfoCanToggleEdit?: boolean;
  canEdit?: boolean;
  changeStatusOnFormFinish?: boolean;
  getOrder?: GetOrderService;
  getOrderMetadata?: GetOrderMetadataService;
  orderCanToggleEdit?: boolean;
  orderEditServices?: IWorkOrderEditServices;
  statusHistoryServices?: IWorkStatusHistoryServices;
  appointmentsServices?: IWorkAppointmentsServices;
  actualsServices?: WorkActualsServices;
  orderBillingServices?: OrderBillingServices;
  detailsPostButtons?: IEditItemToolbarButton[];
  detailsRefreshKey?: number | string;
  getWorkSlas?: GetWorkSlas;
  banner?: React.ReactNode;
  onWorkLoaded?: (work: Record<string, unknown>) => void;
  leaveHandleRef?: MutableRefObject<WorkLeaveHandle | null>;
}

const Work = ({
  workId,
  actualsServices,
  orderBillingServices,
  backPath,
  detailsServices,
  commentsServices,
  photosServices,
  otherInfoServices,
  setIsInProgress,
  setSuccessMessage,
  setErrorMessage,
  showTimesTab,
  timesTab,
  showAssetsTab,
  assetsTab,
  showAvailabilityTab,
  availabilityServices,
  showOffersTab,
  offersServices,
  showQualityCheckTab,
  qualityCheckTab,
  showReportTab,
  reportTab,
  showHeader = false,
  useDesktopSplitOtherInfo = false,
  otherInfoReadOnly = false,
  otherInfoCanToggleEdit = false,
  canEdit = true,
  changeStatusOnFormFinish = false,
  getOrder,
  getOrderMetadata,
  orderCanToggleEdit = false,
  orderEditServices,
  statusHistoryServices,
  appointmentsServices,
  detailsPostButtons,
  detailsRefreshKey,
  getWorkSlas,
  banner,
  onWorkLoaded,
  leaveHandleRef,
}: WorkProps) => {
  const { t } = useTranslation();
  const tabListClass = useThemedTabs();
  const detailsLayoutClass = useWorkDetailsLayout().layout;
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const [deadlinesRefreshKey, setDeadlinesRefreshKey] = useState(0);

  const { state, bulkUpdate } = useObjectState<WorkUrlState>({
    initialState: readInitialParams((params) => ({
      tab: parseTab(params.get('tab')),
      offerId: parseOfferId(params.get('offerId')),
    })),
  });

  const commonProps: WorkComponentProps = {
    backPath,
    setIsInProgress,
    setSuccessMessage,
    setErrorMessage,
  };

  const isNew = !workId || isNaN(workId);
  const title = isNew
    ? t('newWork', 'Create new work')
    : `${t('work', 'Work')} #${workId}`;
  useBreadcrumbs([
    { label: t('works', 'Works'), path: backPath ?? '/' },
    { label: title },
  ]);
  const workFetch = useFetch<Record<string, unknown>>({
    queryKey: ['work', workId, detailsRefreshKey],
    enabled: !isNew,
    queryFn: (abortController) =>
      detailsServices.getWork(abortController, workId),
    onError: (error) =>
      setErrorMessage(error.message || 'Failed to load work details.'),
  });
  const statusesFetch = useFetch<WorkStatus[]>({
    queryKey: ['work-statuses', workId],
    enabled: !isNew,
    queryFn: (abortController) =>
      detailsServices.getWorkStatuses(abortController),
    onError: (error) =>
      setErrorMessage(error.message || 'Failed to load work details.'),
  });
  const canChangeWorkStatus =
    !isNew && detailsServices.updateWorkStatus != null;
  const statusActionsFetch = useFetch<WorkStatusAction[]>({
    queryKey: ['work-status-actions', workId],
    enabled:
      !isNew && !!showReportTab && detailsServices.getWorkStatusActions != null,
    queryFn: (abortController) =>
      detailsServices.getWorkStatusActions!(abortController),
    onError: (error) =>
      setErrorMessage(error.message || 'Failed to load work details.'),
  });
  const [savedFields, setSavedFields] = useState<{
    description?: string | null;
    statusId?: number | null;
    specialInstruction?: string | null;
  }>({});

  const loadedWork = workFetch.data;
  const work = loadedWork ? { ...loadedWork, ...savedFields } : null;
  const linkedOrderId = Number(loadedWork?.orderId);
  const showOrderTab =
    !isNew &&
    !!getOrder &&
    !isContractorRole() &&
    Number.isFinite(linkedOrderId) &&
    linkedOrderId > 0;
  const showBillingTab = !!orderBillingServices && showOrderTab;
  const showStatusHistoryTab = !isNew && !!statusHistoryServices;
  const showAppointmentsTab = !isNew && !!appointmentsServices;

  const availableTabs = useMemo(() => {
    const tabs: WorkTabId[] = [
      WORK_TABS.Details,
      ...(showAssetsTab ? [WORK_TABS.Assets] : []),
      ...(showAvailabilityTab ? [WORK_TABS.Availability] : []),
      ...(showAppointmentsTab ? [WORK_TABS.Appointments] : []),
      ...(actualsServices && !isNew ? [WORK_TABS.Actuals] : []),
      WORK_TABS.OtherInfo,
      WORK_TABS.Comments,
      WORK_TABS.Photos,
      ...(showOffersTab ? [WORK_TABS.Offers] : []),
      ...(showQualityCheckTab && !isNew ? [WORK_TABS.QualityCheck] : []),
      ...(showReportTab ? [WORK_TABS.Report] : []),
      ...(showTimesTab ? [WORK_TABS.Times] : []),
      ...(showOrderTab ? [WORK_TABS.Order] : []),
      ...(showBillingTab ? [WORK_TABS.FinalBilling] : []),
      ...(showStatusHistoryTab ? [WORK_TABS.StatusHistory] : []),
    ];
    return tabs;
  }, [
    actualsServices,
    isNew,
    showAssetsTab,
    showAvailabilityTab,
    showAppointmentsTab,
    showOffersTab,
    showReportTab,
    showQualityCheckTab,
    isNew,
    showTimesTab,
    showOrderTab,
    showBillingTab,
    showStatusHistoryTab,
  ]);

  const selectedTab = availableTabs.includes(state.tab)
    ? state.tab
    : WORK_TABS.Details;
  const [visitedWorkId, setVisitedWorkId] = useState(workId);
  const [visitedTabs, setVisitedTabs] = useState(
    () => new Set<WorkTabId>([selectedTab])
  );
  if (!Object.is(visitedWorkId, workId)) {
    setVisitedWorkId(workId);
    setVisitedTabs(new Set([selectedTab]));
  }

  useSyncParamsToUrl({
    tab: selectedTab,
    offerId: state.offerId,
  });

  const workStatuses = statusesFetch.data ?? [];
  const lockResolved =
    isNew ||
    ((workFetch.data != null || workFetch.error != null) &&
      (statusesFetch.data != null || statusesFetch.error != null));
  const entityLocked =
    !isNew &&
    (!lockResolved ||
      isEntityLocked(
        workStatuses,
        work?.statusId as number | null | undefined
      ));
  const viewOnly = entityLocked || (!isNew && !canEdit);
  const pageEditEnabled =
    !isNew && !viewOnly && (otherInfoCanToggleEdit || orderCanToggleEdit);
  const [isEditing, setIsEditing] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const statusSavingRef = useRef(false);
  const [invalidatedWork, setInvalidatedWork] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const currentStatusId = work?.statusId as number | null | undefined;
  const workStatusRecords = statusActionsFetch.data ?? [];
  const selectedWorkStatusId = findStatusOption(
    workStatuses,
    currentStatusId
  )?.id;
  const allowReportDelivery = workStatusRecords.some(
    (status) =>
      Number(status.id) === Number(selectedWorkStatusId) &&
      isStatusAction(status.allowReportDelivery)
  );
  const statusActions: IAvailableWorkAction[] =
    loadedWork &&
    loadedWork !== invalidatedWork &&
    !workFetch.loading &&
    !workFetch.error
      ? (loadedWork.availableActions as IAvailableWorkAction[] | undefined) ??
        []
      : [];
  const workDescription =
    typeof work?.description === 'string' ? work.description.trim() : '';
  const detailsReady =
    isNew || (!!loadedWork && !workFetch.loading && lockResolved);
  const otherInfoReady = !isNew && !!loadedWork && lockResolved;

  const otherInfoSaveRef = useRef<SaveItemFn | null>(null);
  const otherInfoRevertRef = useRef<(() => void) | null>(null);
  const otherInfoDirtyRef = useRef(false);
  const pendingLeaveRef = useRef<(() => void) | null>(null);
  const [otherInfoDirty, setOtherInfoDirty] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [leaveSaving, setLeaveSaving] = useState(false);

  useEffect(() => {
    otherInfoDirtyRef.current = false;
    setOtherInfoDirty(false);
    setLeaveDialogOpen(false);
    setLeaveSaving(false);
    pendingLeaveRef.current = null;
  }, [workId]);

  const persistOtherInfo = useCallback(
    async (opts?: { skipIfClean?: boolean }) => {
      const save = otherInfoSaveRef.current;
      if (!save) return true;
      return save(opts);
    },
    []
  );

  const requestLeave = useCallback(
    (leave: () => void) => {
      if (viewOnly || !otherInfoDirtyRef.current) {
        leave();
        return;
      }
      pendingLeaveRef.current = leave;
      setLeaveDialogOpen(true);
    },
    [viewOnly]
  );

  useEffect(() => {
    if (!leaveHandleRef) return;
    leaveHandleRef.current = { requestLeave };
    return () => {
      leaveHandleRef.current = null;
    };
  }, [leaveHandleRef, requestLeave]);

  useEffect(() => {
    if (!otherInfoDirty || viewOnly) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [otherInfoDirty, viewOnly]);

  const onTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    const next = String(data.value);
    if (!isWorkTabId(next) || !availableTabs.includes(next)) return;
    if (next === selectedTab) return;
    const leave = () => bulkUpdate({ tab: next, offerId: null });
    if (selectedTab === WORK_TABS.OtherInfo) {
      requestLeave(leave);
      return;
    }
    leave();
  };

  const handleLeaveSave = async () => {
    setLeaveSaving(true);
    try {
      const ok = await persistOtherInfo({ skipIfClean: false });
      if (!ok) {
        pendingLeaveRef.current = null;
        setLeaveDialogOpen(false);
        return;
      }
      otherInfoDirtyRef.current = false;
      setOtherInfoDirty(false);
      const leave = pendingLeaveRef.current;
      pendingLeaveRef.current = null;
      setLeaveDialogOpen(false);
      leave?.();
    } finally {
      setLeaveSaving(false);
    }
  };

  const handleLeaveDiscard = () => {
    otherInfoRevertRef.current?.();
    otherInfoDirtyRef.current = false;
    setOtherInfoDirty(false);
    const leave = pendingLeaveRef.current;
    pendingLeaveRef.current = null;
    setLeaveDialogOpen(false);
    leave?.();
  };

  useEffect(() => {
    setSavedFields({});
    setIsEditing(false);
  }, [workId, detailsRefreshKey]);

  useEffect(() => {
    if (viewOnly) setIsEditing(false);
  }, [viewOnly]);

  const handleFormFinish = async () => {
    if (!changeStatusOnFormFinish) return;
    const finishAction = statusActions.find(
      (action) => action.statusCode === 'INSPECTION_COMPLETE'
    );
    if (finishAction) await handleStatusChange(finishAction);
  };

  const refreshStatus = () => {
    setInvalidatedWork(loadedWork);
    setSavedFields({});
    workFetch.refetch();
    setHistoryRefreshKey((value) => value + 1);
  };

  const handleStatusChange = async (action: IAvailableWorkAction) => {
    if (
      statusSavingRef.current ||
      !statusActions.some(
        (available) => available.statusId === action.statusId
      ) ||
      !detailsServices.updateWorkStatus
    ) {
      return;
    }
    statusSavingRef.current = true;
    setStatusSaving(true);
    try {
      const abortController = new AbortController();
      await detailsServices.updateWorkStatus(
        abortController,
        workId,
        action.statusId
      );
      refreshStatus();
      if (
        action.statusCode === 'INSPECTION_COMPLETE' ||
        action.statusCode === 'COMPLETED'
      ) {
        setDeadlinesRefreshKey((value) => value + 1);
      }
      setSuccessMessage(`Work with id: ${workId} successfully updated.`);
    } catch (error: unknown) {
      if ((error as { status?: number })?.status === 409) {
        refreshStatus();
        setErrorMessage(
          t(
            'workStatusConflict',
            'This work changed since it was loaded. Refreshing its status and available actions. Please review it before trying again.'
          )
        );
      } else {
        setErrorMessage(
          (error as { message?: string })?.message ??
            `Failed to update work ${workId}.`
        );
      }
    } finally {
      statusSavingRef.current = false;
      setStatusSaving(false);
    }
  };

  useEffect(() => {
    if (loadedWork) onWorkLoaded?.(loadedWork);
  }, [loadedWork, onWorkLoaded]);

  useEffect(() => {
    setVisitedTabs((prev) => {
      if (prev.has(selectedTab)) return prev;
      const next = new Set(prev);
      next.add(selectedTab);
      return next;
    });
  }, [selectedTab]);

  const openOffer = (id: number) => {
    bulkUpdate({ tab: WORK_TABS.Offers, offerId: id });
  };

  const closeOffer = () => {
    bulkUpdate({ tab: WORK_TABS.Offers, offerId: null });
  };

  const leaveDialog = (
    <Dialog
      open={leaveDialogOpen}
      onOpenChange={(_, data) => {
        if (!data.open && !leaveSaving) {
          pendingLeaveRef.current = null;
          setLeaveDialogOpen(false);
        }
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {t('unsavedChangesTitle', 'Unsaved changes')}
          </DialogTitle>
          <DialogContent>
            {t(
              'unsavedChangesWillBeLost',
              'Your unsaved changes will be lost.'
            )}
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              disabled={leaveSaving}
              onClick={handleLeaveDiscard}
            >
              {t('cancel', 'Cancel')}
            </Button>
            <Button
              appearance="primary"
              disabled={leaveSaving}
              onClick={() => {
                void handleLeaveSave();
              }}
            >
              {t('save', 'Save')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );

  if (state.offerId && showOffersTab && offersServices && !isNew) {
    return (
      <>
        <WorkOfferDetails
          workOfferId={state.offerId}
          services={offersServices}
          onBack={closeOffer}
          setIsInProgress={setIsInProgress}
          setErrorMessage={setErrorMessage}
          setSuccessMessage={setSuccessMessage}
          readOnly={viewOnly}
        />
        {leaveDialog}
      </>
    );
  }

  return (
    <>
      <div style={headerStyles.chrome}>
        {showHeader && (
          <div style={headerStyles.header}>
            <PageHeader
              onBack={() => requestLeave(() => navigate(backPath ?? '/'))}
              title={title}
              badge={
                !isNew && (
                  <StatusBadge
                    showName
                    mdl2Only
                    statuses={workStatuses}
                    statusId={currentStatusId ?? null}
                    width={120}
                  />
                )
              }
              subtitle={!isNew && workDescription ? workDescription : undefined}
              actions={
                <>
                  {canChangeWorkStatus && (
                    <WorkActions
                      actions={statusActions}
                      disabled={statusSaving}
                      onAction={handleStatusChange}
                    />
                  )}
                  {pageEditEnabled && !isEditing && (
                    <Button
                      appearance="subtle"
                      icon={<EditRegular />}
                      disabled={!PAGE_EDIT_TABS.includes(selectedTab)}
                      onClick={() => setIsEditing(true)}
                    >
                      {t('edit', 'Edit')}
                    </Button>
                  )}
                </>
              }
            />
          </div>
        )}
        {viewOnly ? (
          <MessageBar intent="info" style={{ marginBottom: 16 }}>
            <MessageBarBody>
              {canEdit
                ? t(
                    'entityLocked',
                    'This record is locked and cannot be edited.'
                  )
                : t('workRoleReadOnly', 'Only a manager can edit this work.')}
            </MessageBarBody>
          </MessageBar>
        ) : (
          isEditing && banner
        )}
        <div style={{ overflowX: 'auto' }}>
          <TabList
            className={tabListClass}
            selectedValue={selectedTab}
            onTabSelect={onTabSelect}
          >
            <Tab id={WORK_TABS.Details} value={WORK_TABS.Details}>
              {t('details', 'Details')}
            </Tab>
            {showAssetsTab && (
              <Tab
                id={WORK_TABS.Assets}
                value={WORK_TABS.Assets}
                disabled={isNew}
              >
                {t('assets', 'Assets')}
              </Tab>
            )}
            {showAvailabilityTab && (
              <Tab
                id={WORK_TABS.Availability}
                value={WORK_TABS.Availability}
                disabled={isNew}
              >
                {t('availability', 'Availability')}
              </Tab>
            )}
            {showAppointmentsTab && (
              <Tab id={WORK_TABS.Appointments} value={WORK_TABS.Appointments}>
                {t('appointments', 'Appointments')}
              </Tab>
            )}
            {actualsServices && !isNew && (
              <Tab value={WORK_TABS.Actuals}>Actuals</Tab>
            )}
            <Tab
              id={WORK_TABS.OtherInfo}
              value={WORK_TABS.OtherInfo}
              disabled={isNew}
            >
              {t('otherInfo', 'Other Info')}
            </Tab>
            <Tab
              id={WORK_TABS.Comments}
              value={WORK_TABS.Comments}
              disabled={isNew}
            >
              {t('comments', 'Comments')}
            </Tab>
            <Tab
              id={WORK_TABS.Photos}
              value={WORK_TABS.Photos}
              disabled={isNew}
            >
              {t('photos', 'Photos')}
            </Tab>
            {showOffersTab && (
              <Tab
                id={WORK_TABS.Offers}
                value={WORK_TABS.Offers}
                disabled={isNew}
              >
                {t('offers', 'Offers')}
              </Tab>
            )}
            {showQualityCheckTab && !isNew && (
              <Tab id={WORK_TABS.QualityCheck} value={WORK_TABS.QualityCheck}>
                {t('qualityCheck', 'Quality Check')}
              </Tab>
            )}
            {showReportTab && (
              <Tab
                id={WORK_TABS.Report}
                value={WORK_TABS.Report}
                disabled={isNew}
              >
                {t('report', 'Report')}
              </Tab>
            )}
            {showTimesTab && (
              <Tab
                id={WORK_TABS.Times}
                value={WORK_TABS.Times}
                disabled={isNew}
              >
                {t('times', 'Times')}
              </Tab>
            )}
            {showOrderTab && (
              <Tab id={WORK_TABS.Order} value={WORK_TABS.Order}>
                {t('order', 'Order')}
              </Tab>
            )}
            {showBillingTab && (
              <Tab id={WORK_TABS.FinalBilling} value={WORK_TABS.FinalBilling}>
                {t('finalBilling', 'Final billing')}
              </Tab>
            )}
            {showStatusHistoryTab && (
              <Tab id={WORK_TABS.StatusHistory} value={WORK_TABS.StatusHistory}>
                {t('statusHistory', 'Status History')}
              </Tab>
            )}
          </TabList>
        </div>
      </div>
      {showQualityCheckTab &&
        !isNew &&
        selectedTab === WORK_TABS.QualityCheck && (
          <WorkTabPanel active>{qualityCheckTab}</WorkTabPanel>
        )}
      {selectedTab === WORK_TABS.Details && !detailsReady && (
        <ContentSkeleton variant="form" rows={6} />
      )}
      {keepTab(visitedTabs, selectedTab, WORK_TABS.Details) && detailsReady && (
        <div hidden={selectedTab !== WORK_TABS.Details}>
          <div className={detailsLayoutClass}>
            <WorkDetails
              key={`${workId}-${detailsRefreshKey ?? 0}`}
              {...commonProps}
              workId={workId}
              services={detailsServices}
              loadedWork={work}
              getOrder={getOrder}
              getOrderMetadata={getOrderMetadata}
              postButtons={viewOnly ? undefined : detailsPostButtons}
              onWorkSaved={(fields) => {
                setSavedFields((current) => ({
                  ...current,
                  description: fields.description,
                  ...('specialInstruction' in fields
                    ? { specialInstruction: fields.specialInstruction }
                    : {}),
                }));
                if (fields.statusId != null) workFetch.refetch();
              }}
              readOnly={viewOnly}
              editing={pageEditEnabled ? isEditing : undefined}
              onEditingChange={pageEditEnabled ? setIsEditing : undefined}
            />
            {getWorkSlas && !isNew && (
              <WorkDeadlines
                key={`${workId}-${tenant?.id ?? 0}`}
                workId={workId}
                getWorkSlas={getWorkSlas}
                refreshKey={deadlinesRefreshKey}
              />
            )}
          </div>
        </div>
      )}
      {showAssetsTab && keepTab(visitedTabs, selectedTab, WORK_TABS.Assets) && (
        <WorkTabPanel active={selectedTab === WORK_TABS.Assets}>
          {isValidElement(assetsTab)
            ? cloneElement(assetsTab as ReactElement<{ readOnly?: boolean }>, {
                readOnly: viewOnly,
              })
            : assetsTab}
        </WorkTabPanel>
      )}
      {showAvailabilityTab &&
        availabilityServices &&
        !isNew &&
        keepTab(visitedTabs, selectedTab, WORK_TABS.Availability) && (
          <WorkTabPanel active={selectedTab === WORK_TABS.Availability}>
            <WorkAvailability
              workId={workId}
              services={availabilityServices}
              setIsInProgress={setIsInProgress}
              setSuccessMessage={setSuccessMessage}
              setErrorMessage={setErrorMessage}
              readOnly={viewOnly}
            />
          </WorkTabPanel>
        )}
      {actualsServices &&
        keepTab(visitedTabs, selectedTab, WORK_TABS.Actuals) && (
          <div hidden={selectedTab !== WORK_TABS.Actuals}>
            <WorkActuals workId={workId} services={actualsServices} />
          </div>
        )}
      {showAppointmentsTab && selectedTab === WORK_TABS.Appointments && (
        <WorkTabPanel active>
          <WorkAppointments
            {...commonProps}
            workId={workId}
            services={appointmentsServices!}
          />
        </WorkTabPanel>
      )}
      {selectedTab === WORK_TABS.OtherInfo && !otherInfoReady && (
        <ContentSkeleton variant="form" rows={6} />
      )}
      {keepTab(visitedTabs, selectedTab, WORK_TABS.OtherInfo) &&
        otherInfoReady && (
          <WorkTabPanel active={selectedTab === WORK_TABS.OtherInfo}>
            <WorkOtherInfo
              key={workId}
              {...commonProps}
              workId={workId}
              services={otherInfoServices}
              loadedWork={work}
              useDesktopSplitOtherInfo={useDesktopSplitOtherInfo}
              readOnly={otherInfoReadOnly || viewOnly}
              canToggleEdit={otherInfoCanToggleEdit && !viewOnly}
              editing={pageEditEnabled ? isEditing : undefined}
              onEditingChange={pageEditEnabled ? setIsEditing : undefined}
              saveRef={otherInfoSaveRef}
              revertRef={otherInfoRevertRef}
              onDirtyChange={(dirty) => {
                otherInfoDirtyRef.current = dirty;
                setOtherInfoDirty(dirty);
              }}
              onFormFinish={
                changeStatusOnFormFinish ? handleFormFinish : undefined
              }
            />
          </WorkTabPanel>
        )}
      {keepTab(visitedTabs, selectedTab, WORK_TABS.Comments) && !isNew && (
        <WorkTabPanel active={selectedTab === WORK_TABS.Comments}>
          <WorkComments
            {...commonProps}
            workId={workId}
            services={commentsServices}
            readOnly={viewOnly}
          />
        </WorkTabPanel>
      )}
      {keepTab(visitedTabs, selectedTab, WORK_TABS.Photos) && (
        <WorkTabPanel active={selectedTab === WORK_TABS.Photos}>
          <WorkPhotos
            {...commonProps}
            workId={workId}
            services={photosServices}
            onBackToDetails={() =>
              bulkUpdate({ tab: WORK_TABS.Details, offerId: null })
            }
            readOnly={viewOnly}
          />
        </WorkTabPanel>
      )}
      {showOffersTab &&
        offersServices &&
        keepTab(visitedTabs, selectedTab, WORK_TABS.Offers) && (
          <WorkTabPanel active={selectedTab === WORK_TABS.Offers}>
            <WorkOffersList
              workId={workId}
              services={offersServices}
              onOpenOffer={(offer) => openOffer(offer.id)}
              setIsInProgress={setIsInProgress}
              setErrorMessage={setErrorMessage}
            />
          </WorkTabPanel>
        )}
      {showReportTab && keepTab(visitedTabs, selectedTab, WORK_TABS.Report) && (
        <WorkTabPanel active={selectedTab === WORK_TABS.Report}>
          {isValidElement(reportTab)
            ? cloneElement(
                reportTab as ReactElement<{
                  allowReportDelivery?: boolean;
                  onOpenQualityCheck?: () => void;
                }>,
                {
                  allowReportDelivery,
                  ...(showQualityCheckTab
                    ? {
                        onOpenQualityCheck: () =>
                          bulkUpdate({
                            tab: WORK_TABS.QualityCheck,
                            offerId: null,
                          }),
                      }
                    : {}),
                }
              )
            : reportTab}
        </WorkTabPanel>
      )}
      {showTimesTab && keepTab(visitedTabs, selectedTab, WORK_TABS.Times) && (
        <WorkTabPanel active={selectedTab === WORK_TABS.Times}>
          {isValidElement(timesTab)
            ? cloneElement(timesTab as ReactElement<{ readOnly?: boolean }>, {
                readOnly: viewOnly,
              })
            : timesTab}
        </WorkTabPanel>
      )}
      {showOrderTab &&
        getOrder &&
        keepTab(visitedTabs, selectedTab, WORK_TABS.Order) && (
          <div hidden={selectedTab !== WORK_TABS.Order}>
            {orderCanToggleEdit && !viewOnly && orderEditServices ? (
              <WorkOrderTab
                orderId={linkedOrderId}
                getOrder={getOrder}
                getMetadata={getOrderMetadata}
                services={orderEditServices}
                getTenantWorkTypes={detailsServices.getTenantWorkTypes}
                workTypeId={
                  typeof loadedWork?.workTypeId === 'number'
                    ? loadedWork.workTypeId
                    : null
                }
                canToggleEdit
                editing={isEditing}
                onEditingChange={setIsEditing}
                backPath={backPath}
                setIsInProgress={setIsInProgress}
                setSuccessMessage={setSuccessMessage}
                setErrorMessage={setErrorMessage}
              />
            ) : (
              <MokoCard style={{ padding: 0 }}>
                <OrderDetailsView
                  layout="split"
                  orderId={linkedOrderId}
                  getOrder={getOrder}
                  getMetadata={getOrderMetadata}
                  orderComponentServices={orderEditServices}
                  getTenantWorkTypes={detailsServices.getTenantWorkTypes}
                  workTypeId={
                    typeof loadedWork?.workTypeId === 'number'
                      ? loadedWork.workTypeId
                      : null
                  }
                />
              </MokoCard>
            )}
          </div>
        )}
      {showBillingTab &&
        orderBillingServices &&
        keepTab(visitedTabs, selectedTab, WORK_TABS.FinalBilling) && (
          <div hidden={selectedTab !== WORK_TABS.FinalBilling}>
            <OrderBilling
              key={linkedOrderId}
              orderId={linkedOrderId}
              services={orderBillingServices}
            />
          </div>
        )}
      {showStatusHistoryTab && selectedTab === WORK_TABS.StatusHistory && (
        <WorkTabPanel active>
          <WorkStatusHistory
            key={`${workId}-${historyRefreshKey}`}
            {...commonProps}
            workId={workId}
            services={statusHistoryServices!}
            statuses={workStatuses}
          />
        </WorkTabPanel>
      )}
      {leaveDialog}
    </>
  );
};

const headerStyles: Record<string, React.CSSProperties> = {
  chrome: {
    position: 'sticky',
    top: -16,
    zIndex: 2,
    backgroundColor: shellVar(
      'page-background',
      tokens.colorNeutralBackground2
    ),
    marginTop: -16,
    marginLeft: -16,
    marginRight: -16,
    paddingTop: 16,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 16,
  },
  header: {
    marginBottom: tokens.spacingVerticalM,
  },
};

export default Work;
